// frontend/src/AuthContext.tsx
import { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { useApi, ApiResult } from './api';

export interface User {
  id: string;
  email: string;
  username?: string;
  role?: string;
  name?: string;
  /** Server-side STAFF_ADMIN_USERNAMES membership (SYS-3202). */
  is_staff_admin?: boolean;
  [key: string]: unknown;
}

export interface LoginResult {
  success: boolean;
  password_expired?: boolean;
  message?: string | null;
  error?: string;
}

export interface AuthContextType {
  token: string | null;
  user: User | null;
  isUserLoading: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
  authHeader: () => Record<string, string>;
  isAuthenticated: boolean;
  forcePasswordChange: boolean;
  passwordChangeMessage: string;
  clearPasswordChangeRequirement: () => void;
  setAuthToken: (token: string | null) => void;
}

interface ErrorWithCode extends Error {
  code?: string;
}

interface PasswordChangeEventDetail {
  message?: string;
}

type PasswordChangeEvent = CustomEvent<PasswordChangeEventDetail>;

const AuthContext = createContext<AuthContextType | null>(null);
const PASSWORD_CHANGE_DEFAULT_MESSAGE = 'Your password has expired. Please change it now.';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [user, setUser] = useState<User | null>(null);
  const [isUserLoading, setIsUserLoading] = useState<boolean>(false);
  const [forcePasswordChange, setForcePasswordChange] = useState<boolean>(false);
  const [passwordChangeMessage, setPasswordChangeMessage] = useState<string>('');

  const requirePasswordChange = useCallback((message?: string) => {
    setForcePasswordChange(true);
    setPasswordChangeMessage(message || PASSWORD_CHANGE_DEFAULT_MESSAGE);
  }, []);

  const clearPasswordChangeRequirement = useCallback(() => {
    setForcePasswordChange(false);
    setPasswordChangeMessage('');
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    clearPasswordChangeRequirement();
    window.location.href = '/login';
  }, [clearPasswordChangeRequirement]);

  const api: ApiResult = useApi(token, logout);
  const { apiFetch } = api;

  useEffect(() => {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
      setUser(null);
    }
  }, [token]);

  const refreshUser = useCallback(async (): Promise<User | null> => {
    if (!token) {
      setUser(null);
      setIsUserLoading(false);
      return null;
    }

    setIsUserLoading(true);

    try {
      const response = await apiFetch('/api/auth/me');
      if (!response.ok) throw new Error('Failed to fetch user');
      const data: User = await response.json();
      setUser(data);
      clearPasswordChangeRequirement();
      return data;
    } catch (error) {
      console.error('Failed to fetch auth user', error);
      setUser(null);
      const err = error as ErrorWithCode;
      if (err?.code === 'PASSWORD_CHANGE_REQUIRED') {
        requirePasswordChange(err.message);
      }
      return null;
    } finally {
      setIsUserLoading(false);
    }
  }, [token, apiFetch, requirePasswordChange, clearPasswordChangeRequirement]);

  useEffect(() => {
    let cancelled = false;

    const fetchUser = async () => {
      if (!token) {
        setUser(null);
        setIsUserLoading(false);
        return;
      }

      setIsUserLoading(true);
      try {
        const response = await apiFetch('/api/auth/me');
        if (!response.ok) throw new Error('Failed to fetch user');
        const data: User = await response.json();
        if (!cancelled) {
          setUser(data);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load current user', error);
          setUser(null);
          const err = error as ErrorWithCode;
          if (err?.code === 'PASSWORD_CHANGE_REQUIRED') {
            requirePasswordChange(err.message);
          }
        }
      } finally {
        if (!cancelled) {
          setIsUserLoading(false);
        }
      }
    };

    fetchUser();

    return () => {
      cancelled = true;
    };
  }, [token, apiFetch, requirePasswordChange]);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as PasswordChangeEvent;
      requirePasswordChange(customEvent.detail?.message);
    };
    window.addEventListener('password-change-required', handler);
    return () => window.removeEventListener('password-change-required', handler);
  }, [requirePasswordChange]);

  const login = async (username: string, password: string): Promise<LoginResult> => {
    try {
      // Bypass apiFetch for login so we can read the raw 403 body without
      // triggering the global logout on 401 or throwing on non-2xx.
      const { getApiBaseUrl } = await import('./utils/apiBaseUrl');
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ username, password }),
      });

      const data = await response.json().catch(() => ({})) as {
        access_token?: string;
        token_type?: string;
        detail?: string | { code?: string; message?: string; password_reset_token?: string };
      };

      if (!response.ok) {
        // Expired-password path: server returns 403 with a reset-scope token
        // we must use to hit /api/change-password, and nothing else.
        const detail = typeof data.detail === 'object' ? data.detail : null;
        if (response.status === 403 && detail?.code === 'PASSWORD_EXPIRED' && detail.password_reset_token) {
          setToken(detail.password_reset_token);
          localStorage.setItem('authToken', detail.password_reset_token);
          requirePasswordChange(detail.message);
          // Hard redirect so route guards run again with the new reset token.
          window.location.href = '/change-password';
          return {
            success: true,
            password_expired: true,
            message: detail.message || PASSWORD_CHANGE_DEFAULT_MESSAGE,
          };
        }
        const msg = typeof data.detail === 'string'
          ? data.detail
          : detail?.message || 'Login failed';
        throw new Error(msg);
      }

      if (!data.access_token) {
        throw new Error('Login failed');
      }

      setToken(data.access_token);
      localStorage.setItem('authToken', data.access_token);
      clearPasswordChangeRequirement();
      return { success: true, password_expired: false, message: null };
    } catch (error) {
      console.error('Login failed', error);
      return { success: false, error: (error as Error).message || 'Login failed' };
    }
  };

  const authHeader = useCallback(
    (): Record<string, string> => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const value: AuthContextType = {
    token,
    user,
    isUserLoading,
    login,
    logout,
    refreshUser,
    authHeader,
    isAuthenticated: !!token,
    forcePasswordChange,
    passwordChangeMessage,
    clearPasswordChangeRequirement,
    setAuthToken: (t: string | null) => setToken(t),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
